import { useMemo } from "react";
import { useProfile } from "hooks/firestore/v2/user/useProfile";
import { useUserVerifications } from "hooks/firestore/v2/user/useUserVerifications";
import {
  type VerificationDataSourceSchema,
  VERIFICATION_DATA_SOURCES,
  type VerificationDataSource,
} from "hooks/firestore/v2/models/userVerifications";

export const verificationOrder = Object.values(VERIFICATION_DATA_SOURCES);

export const useVerifications = () => {
  const { userVerifications, isLoading, error } = useUserVerifications();

  const verifications = useMemo(() => {
    if (!userVerifications?.[0]?.dataSources) return [];

    // This `as` cast is unavoidable as far as I can tell. I believe it would be
    // fixed if we upgraded to Zod 4 as exhaustive checks when using an enum as
    // the key within `.record()` schema is a feature there, but that is out of
    // scope for right now — https://zod.dev/api#records
    const entries = Object.entries(userVerifications[0].dataSources) as [
      VerificationDataSource,
      VerificationDataSourceSchema,
    ][];

    return entries
      .sort(([typeA, a], [typeB, b]) => {
        // We order overall result by order of VerificationDataSource enum
        const orderA = verificationOrder.indexOf(typeA);
        const orderB = verificationOrder.indexOf(typeB);
        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // If enum order is the same, use displayOrder for sorting
        if (a.displayOrder === null) return 1;
        if (b.displayOrder === null) return -1;
        return a.displayOrder - b.displayOrder;
      })
      .map(([type, verification]) => {
        return {
          ...verification,
          type,
        };
      });
  }, [userVerifications]);

  return { verifications, isLoading, error } as const;
};

export const useVerifyDocCount = () => {
  const { verifications } = useVerifications();
  const { profile } = useProfile();

  // if nothing loaded yet return 0
  if (!verifications || !profile) return 0;

  if (["AU", "NZ"].includes(profile.country)) {
    // for Australia and New Zealand, we need to verify 2 documents
    // if at least one verification has `sufficient` field set to true
    // that means we only need to verify one more document
    const sufficient = Object.values(verifications).some(
      (verification) => verification.sufficient === true,
    );

    return sufficient ? 1 : 2;
  }

  // for other countries we only need 1 document
  return 1;
};
